// client/src/hooks/useUsers.js - VERSÃO 100% CORRIGIDA
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import useAlert from './useAlert';
import {
    isMatrizAdmin,
    isFranquiaAdmin as isFranquiaAdminUser,
    isContaAdmin as isContaAdminUser
} from '../utils/permissions';

const USERS_QUERY_KEY = 'users';

// ==============================================================================
// ✅ UTILITÁRIOS
// ==============================================================================

// Validador robusto de ID
const validateUserId = (id, operation = 'operação') => {
    if (!id) return null;
    
    const idToValidate = id?._id || id?.id || id;
    
    if (!idToValidate) return null;
    
    const idStr = String(idToValidate).trim();
    
    if (!/^[0-9a-fA-F]{24}$/.test(idStr)) {
        console.warn(`[validateUserId] ID com formato inválido para ${operation}:`, idStr);
        return null;
    }
    
    return idStr;
};

// Helper para construir query params
const buildQueryParams = (params = {}) => {
    const queryParams = { ...params };
    
    const booleanFields = ['isActive', 'isProfissional', 'includeDeleted', 'includeInactive'];
    booleanFields.forEach(field => {
        if (queryParams[field] !== undefined) {
            queryParams[field] = queryParams[field] === true || queryParams[field] === 'true';
        }
    });
    
    Object.keys(queryParams).forEach(key => {
        const value = queryParams[key];
        if (value === undefined || value === null || value === '' || 
            (Array.isArray(value) && value.length === 0)) {
            delete queryParams[key];
        }
    });
    
    return queryParams;
};

// Helper para extrair dados da resposta da API
const extractData = (response) => {
    if (!response || !response.data) {
        console.warn('[extractData] Resposta vazia ou sem data:', response);
        return null;
    }
    
    if (response.data.success !== undefined) {
        return response.data.data || response.data;
    }
    
    return response.data;
};

// Normalizar role para string ID
const normalizeRole = (role) => {
    if (!role) return '';
    
    if (role.$oid) return role.$oid;
    if (role._id) return role._id;
    if (role.id) return role.id;
    if (typeof role === 'string') return role;
    if (role && typeof role === 'object' && role.toString) return role.toString();
    
    return '';
};

// ✅ Funções auxiliares (CORRIGIDAS com validação segura)
const canManageUserPhoto = (currentUser, targetUserId) => {
    if (!currentUser || !targetUserId) return false;
    
    const currentUserId = currentUser._id || currentUser.id;
    const targetId = targetUserId?._id || targetUserId?.id || targetUserId;
    
    // VALIDAÇÃO SEGURA
    if (!currentUserId || !targetId) return false;
    
    try {
        const isSelf = currentUserId.toString() === targetId.toString();
        const isMatriz = isMatrizAdmin(currentUser);

        return isSelf || isMatriz;
    } catch (error) {
        console.error('[canManageUserPhoto] Erro na comparação:', error);
        return false;
    }
};

const isSelf = (currentUser, targetUserId) => {
    if (!currentUser || !targetUserId) return false;
    
    const currentUserId = currentUser._id || currentUser.id;
    const targetId = targetUserId?._id || targetUserId?.id || targetUserId;
    
    if (!currentUserId || !targetId) return false;
    
    try {
        return currentUserId.toString() === targetId.toString();
    } catch (error) {
        return false;
    }
};

// ✅ Helper para formatar dados do usuário
const formatUserData = (user, currentUser = null) => {
    if (!user) return null;
    
    const roleId = normalizeRole(user.role);
    
    const formattedUser = {
        ...user,
        id: user.id || user._id,
        name: user.name || '',
        email: user.email || '',
        telefone: user.telefone || '',
        role: roleId,
        roleId: roleId,
        roleObject: user.role && typeof user.role === 'object' && !user.role.$oid ? user.role : null,
        roleName: user.role?.name || '',
        roleDescription: user.role?.description || '',
        profissionalId: user.profissionalId || null,
        isProfissional: !!user.profissionalId,
        metadata: user.metadata || {
            tipoUsuario: 'interno',
            notificacoes: {
                email: true,
                sms: false,
                push: true
            }
        },
        avatar: user.avatar || {
            url: '',
            initials: '',
            isPlaceholder: true,
            hasCustomPhoto: false
        },
        status: user.status || 'ativo',
        photoUrl: user.photoUrl || user.photo || '',
        vincularStatus: user.vincularStatus || {
            vinculado: false,
            success: false,
            error: null
        },
        accountInfo: user.accountInfo || null
    };
    
    if (currentUser) {
        const userId = formattedUser.id || formattedUser._id;
        formattedUser.permissions = {
            canManagePhoto: canManageUserPhoto(currentUser, userId),
            isSelf: isSelf(currentUser, userId),
            isEditableByCurrentUser: canManageUserPhoto(currentUser, userId) || 
                                    isMatrizAdmin(currentUser) ||
                                    currentUser.role?.permissions?.includes('equipe:edit'),
            canDeletePhoto: canManageUserPhoto(currentUser, userId),
            canUpdatePhoto: canManageUserPhoto(currentUser, userId),
            canViewPhotoInfo: true,
            canLinkAccount: currentUser.role?.permissions?.includes('equipe:vincular_conta') || isMatrizAdmin(currentUser)
        };
    }
    
    return formattedUser;
};

// ==============================================================================
// ✅ FUNÇÕES DE FETCH
// ==============================================================================

const fetchUsers = async (params = {}) => {
    const queryParams = buildQueryParams(params);
    const response = await api.get('/users', { params: queryParams });
    const data = extractData(response);
    
    if (Array.isArray(data)) {
        return data.map(user => formatUserData(user));
    }
    
    if (data && data.data && Array.isArray(data.data)) {
        return {
            ...data,
            data: data.data.map(user => formatUserData(user))
        };
    }
    
    return data;
};

const fetchUserById = async (id) => {
    const validId = validateUserId(id, 'busca de usuário');
    if (!validId) return null;
    
    try {
        const response = await api.get(`/users/${validId}`);
        const data = extractData(response);
        return formatUserData(data);
    } catch (error) {
        if (error.response?.status === 404) {
            console.warn(`[fetchUserById] Usuário ${validId} não encontrado.`);
            return null;
        }
        throw error;
    }
};

const fetchMe = async () => {
    const response = await api.get('/users/me');
    const data = extractData(response);
    return formatUserData(data);
};

const fetchMyStatistics = async () => {
    const response = await api.get('/users/me/estatisticas');
    return extractData(response);
};

const fetchDeletedUsers = async (params = {}) => {
    const queryParams = buildQueryParams(params);
    const response = await api.get('/users/excluidos', { params: queryParams });
    const data = extractData(response);
    
    if (Array.isArray(data)) {
        return data.map(user => formatUserData(user));
    }
    
    if (data && Array.isArray(data.data)) {
        return data.data.map(user => formatUserData(user));
    }
    
    return [];
};

const fetchIsProfessionalActive = async (userId) => {
    const validId = validateUserId(userId, 'verificar profissional ativo');
    if (!validId) return { isProfissionalAtivo: false, data: null };
    
    const response = await api.get(`/users/${validId}/is-profissional-ativo`);
    return extractData(response);
};

const fetchUserVinculos = async (userId) => {
    const validId = validateUserId(userId, 'buscar vínculos');
    if (!validId) return null;
    
    const response = await api.get(`/users/${validId}/vinculos`);
    return extractData(response);
};

const fetchUsersByAccount = async (accountId, params = {}) => {
    const validId = validateUserId(accountId, 'buscar usuários por conta');
    if (!validId) return { usuarios: [], account: null, total: 0 };
    
    const queryParams = buildQueryParams(params);
    const response = await api.get(`/users/por-conta/${validId}`, { params: queryParams });
    return extractData(response);
};

// ==============================================================================
// ✅ HOOKS DE CONSULTA
// ==============================================================================

const useUsers = (params = {}, options = {}) => {
    return useQuery({
        queryKey: [USERS_QUERY_KEY, 'list', params],
        queryFn: () => fetchUsers(params),
        staleTime: 2 * 60 * 1000,
        keepPreviousData: true,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
            if ([401, 403, 404].includes(error.response?.status)) {
                return false;
            }
            return failureCount < 2;
        },
        ...options
    });
};

const useUserById = (id, options = {}) => {
    return useQuery({
        queryKey: [USERS_QUERY_KEY, id],
        queryFn: () => fetchUserById(id),
        enabled: !!id && (options.enabled !== false),
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        retry: 1,
        ...options
    });
};

const useMe = (options = {}) => {
    return useQuery({
        queryKey: [USERS_QUERY_KEY, 'me'],
        queryFn: fetchMe,
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        retry: false,
        ...options
    });
};

const useMyStatistics = (options = {}) => {
    return useQuery({
        queryKey: [USERS_QUERY_KEY, 'me', 'estatisticas'],
        queryFn: fetchMyStatistics,
        staleTime: 10 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        ...options
    });
};

const useUserVinculos = (userId, options = {}) => {
    return useQuery({
        queryKey: [USERS_QUERY_KEY, userId, 'vinculos'],
        queryFn: () => fetchUserVinculos(userId),
        enabled: !!userId && (options.enabled !== false),
        staleTime: 2 * 60 * 1000,
        ...options
    });
};

const useUsersByAccount = (accountId, params = {}, options = {}) => {
    return useQuery({
        queryKey: [USERS_QUERY_KEY, 'account', accountId, params],
        queryFn: () => fetchUsersByAccount(accountId, params),
        enabled: !!accountId && (options.enabled !== false),
        staleTime: 2 * 60 * 1000,
        ...options
    });
};

const useDeletedUsers = (params = {}, options = {}) => {
    return useQuery({
        queryKey: [USERS_QUERY_KEY, 'deleted', params],
        queryFn: () => fetchDeletedUsers(params),
        staleTime: 5 * 60 * 1000,
        ...options
    });
};

const useIsProfessionalActive = (userId, options = {}) => {
    return useQuery({
        queryKey: [USERS_QUERY_KEY, userId, 'is-profissional-ativo'],
        queryFn: () => fetchIsProfessionalActive(userId),
        enabled: !!userId && (options.enabled !== false),
        staleTime: 2 * 60 * 1000,
        select: (data) => {
            if (options.selectIsActive) {
                return data?.isProfissionalAtivo || false;
            }
            return data;
        },
        ...options
    });
};

// Placeholder hooks
const useUserImages = (userId, options = {}) => {
    return useQuery({
        queryKey: [USERS_QUERY_KEY, userId, 'imagens'],
        queryFn: async () => [],
        enabled: false,
        ...options
    });
};

const FRANQUIA_ADMINS_KEY = (franquiaId) => [USERS_QUERY_KEY, 'franquia', franquiaId, 'admins'];

const useFranquiaAdmins = (franquiaId, params = {}, options = {}) => {
    return useQuery({
        queryKey: [...FRANQUIA_ADMINS_KEY(franquiaId), params],
        queryFn: async () => {
            const { data } = await api.get(`/franquias/${franquiaId}/admins`, { params });
            return data.data || [];
        },
        enabled: !!franquiaId,
        ...options
    });
};

/**
 * Mutations pra gerenciar os administradores de uma franquia
 * (adicionar / remover). Ver franquiasController.addFranquiaAdmin / removeFranquiaAdmin.
 */
const useFranquiaAdminMutations = (franquiaId) => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: FRANQUIA_ADMINS_KEY(franquiaId) });
        queryClient.invalidateQueries({ queryKey: ['franquias'] });
    };

    const addAdmin = useMutation({
        mutationFn: (payload) => api.post(`/franquias/${franquiaId}/admins`, payload).then(r => r.data.data),
        onSuccess: () => { invalidate(); setAlert('Administrador adicionado à franquia.', 'success'); },
        onError: (err) => setAlert(err.response?.data?.error || err.response?.data?.message || 'Erro ao adicionar administrador.', 'error'),
    });

    const removeAdmin = useMutation({
        mutationFn: (vinculoId) => api.delete(`/franquias/${franquiaId}/admins/${vinculoId}`),
        onSuccess: () => { invalidate(); setAlert('Administrador removido da franquia.', 'success'); },
        onError: (err) => setAlert(err.response?.data?.error || err.response?.data?.message || 'Erro ao remover administrador.', 'error'),
    });

    return { addAdmin, removeAdmin, isPending: addAdmin.isPending || removeAdmin.isPending };
};

// ==============================================================================
// ✅ HOOK DE MUTAÇÕES - VERSÃO CORRIGIDA
// ==============================================================================

const useUserMutations = (currentUser = null) => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    const invalidateUserQueries = (userId = null) => {
        queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
        
        if (userId) {
            const validId = validateUserId(userId);
            if (validId) {
                queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY, validId] });
                queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY, validId, 'vinculos'] });
            }
        }
        
        queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY, 'me'] });
        queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY, 'me', 'estatisticas'] });
    };

    const createUser = useMutation({
        mutationFn: async (userData) => {
            console.log('📤 [CREATE_USER] Dados:', {
                ...userData,
                telefone: userData.telefone?.substring(0, 5) + '...',
                email: userData.email ? userData.email.substring(0, 10) + '...' : null
            });
            
            const payload = {
                name: userData.name?.trim(),
                email: userData.email?.trim()?.toLowerCase() || null,
                telefone: userData.telefone?.replace(/\D/g, ''),
                cpf: userData.cpf,
                dataNascimento: userData.dataNascimento,
                role: userData.role,
                requirePasswordChange: userData.requirePasswordChange !== false,
                metadata: userData.metadata || {
                    tipoUsuario: 'interno',
                    notificacoes: { email: true, sms: false, push: true }
                }
            };
            
            if (userData.accountId) {
                payload.accountId = userData.accountId;
            }
            
            if (userData.franquiaId) {
                payload.franquiaId = userData.franquiaId;
            }
            
            const response = await api.post('/users', payload);
            return response.data;
        },
        onSuccess: (data) => {
            console.log('✅ [CREATE_USER] Usuário criado:', data.data?.id);
            invalidateUserQueries();
            
            let message = data.message || 'Usuário criado com sucesso!';
            if (data.metadata?.vinculado) {
                message += ' Vinculado à conta com sucesso.';
            }
            
            setAlert(message, 'success');
        },
        onError: (error) => {
            console.error('❌ [CREATE_USER] Erro:', error);
            
            const errorMessage = error.response?.data?.message || 
                               error.response?.data?.error || 
                               'Erro ao criar usuário';
            setAlert(errorMessage, 'error');
        }
    });

    const vincularUsuarioConta = useMutation({
        mutationFn: async ({ userId, accountId, roleId, unidades = [] }) => {
            const validUserId = validateUserId(userId);
            const validAccountId = validateUserId(accountId);
            if (!validUserId || !validAccountId) throw new Error('ID inválido');
            
            const payload = { accountId: validAccountId, roleId, unidades };
            const response = await api.post(`/users/${validUserId}/vincular-conta`, payload);
            return extractData(response);
        },
        onSuccess: (data) => {
            invalidateUserQueries(data.data?.userId);
            setAlert(data?.message || 'Usuário vinculado à conta!', 'success');
        },
        onError: (error) => {
            const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Erro ao vincular';
            setAlert(errorMessage, 'error');
        }
    });

    const updateMyPhoto = useMutation({
        mutationFn: async (formData) => {
            if (!(formData instanceof FormData)) {
                throw new Error('Dados de upload devem ser um FormData');
            }
            
            const response = await api.put('/users/me/photo', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            return extractData(response);
        },
        onSuccess: (data) => {
            invalidateUserQueries();
            setAlert(data?.message || 'Sua foto foi atualizada!', 'success');
        },
        onError: (error) => {
            setAlert(error.response?.data?.error || error.response?.data?.message || 'Erro ao atualizar foto', 'error');
        }
    });

    const removeMyPhoto = useMutation({
        mutationFn: async () => {
            const response = await api.delete('/users/me/photo');
            return extractData(response);
        },
        onSuccess: (data) => {
            invalidateUserQueries();
            setAlert(data?.message || 'Foto removida!', 'success');
        },
        onError: (error) => {
            setAlert(error.response?.data?.error || error.response?.data?.message || 'Erro ao remover foto', 'error');
        }
    });

    // ✅ CORREÇÃO PRINCIPAL: updateUser - AGORA ENVIA EMAIL CORRETAMENTE
    const updateUser = useMutation({
        mutationFn: async ({ userId, userData }) => {
            const validId = validateUserId(userId);
            if (!validId) throw new Error('ID inválido');
            
            // Construir payload com os campos permitidos pelo backend
            const payload = {
                name: userData.name?.trim(),
                telefone: userData.telefone?.replace(/\D/g, ''),
                cpf: userData.cpf?.replace(/\D/g, '') || null,
                dataNascimento: userData.dataNascimento || null,
                role: normalizeRole(userData.role),
                isActive: userData.isActive,
                metadata: userData.metadata,
                requirePasswordChange: userData.requirePasswordChange
            };
            
            // ✅ CORREÇÃO CRÍTICA: Só enviar email se foi explicitamente fornecido
            if (userData.email !== undefined && userData.email !== null) {
                // Se email for string vazia, enviar null (remove email)
                if (userData.email === '') {
                    payload.email = null;
                } else {
                    // Validar formato antes de enviar
                    const emailStr = userData.email?.trim()?.toLowerCase();
                    if (emailStr && emailStr.includes('@') && emailStr.includes('.')) {
                        payload.email = emailStr;
                    } else if (emailStr) {
                        throw new Error('Email inválido');
                    }
                }
            }
            
            // Remover campos undefined
            Object.keys(payload).forEach(key => 
                payload[key] === undefined && delete payload[key]
            );
            
            console.log('📤 [UPDATE_USER] Atualizando usuário:', { 
                userId: validId,
                payload 
            });
            
            // Rota correta: PUT /users/:id
            const response = await api.put(`/users/${validId}`, payload);
            return extractData(response);
        },
        onSuccess: (data, variables) => {
            console.log('✅ [UPDATE_USER] Usuário atualizado:', variables.userId);
            invalidateUserQueries(variables.userId);
            setAlert(data?.message || 'Usuário atualizado com sucesso!', 'success');
        },
        onError: (error) => {
            console.error('❌ [UPDATE_USER] Erro:', error);
            
            let errorMessage = error.response?.data?.message || 
                             error.response?.data?.error || 
                             'Erro ao atualizar usuário';
            
            // Mensagens amigáveis para erros comuns
            if (errorMessage.includes('duplicate key') || errorMessage.includes('duplicate')) {
                if (errorMessage.includes('telefone')) {
                    errorMessage = 'Este telefone já está cadastrado';
                } else if (errorMessage.includes('email')) {
                    errorMessage = 'Este email já está cadastrado';
                }
            }
            
            setAlert(errorMessage, 'error');
        }
    });

    const updateMyProfile = useMutation({
        mutationFn: async (profileData) => {
            const payload = {
                ...profileData,
                telefone: profileData.telefone?.replace(/\D/g, '')
            };
            
            // Só enviar email se foi fornecido
            if (profileData.email !== undefined) {
                payload.email = profileData.email?.toLowerCase();
            }
            
            const response = await api.put('/users/me/profile', payload);
            return extractData(response);
        },
        onSuccess: (data) => {
            invalidateUserQueries();
            setAlert(data?.message || 'Perfil atualizado!', 'success');
        },
        onError: (error) => {
            setAlert(error.response?.data?.error || error.response?.data?.message || 'Erro ao atualizar perfil', 'error');
        }
    });

    const updateMyPassword = useMutation({
        mutationFn: async (passwordData) => {
            const response = await api.put('/users/me/password', passwordData);
            return extractData(response);
        },
        onSuccess: (data) => {
            setAlert(data?.message || 'Senha atualizada!', 'success');
        },
        onError: (error) => {
            setAlert(error.response?.data?.error || error.response?.data?.message || 'Erro ao atualizar senha', 'error');
        }
    });

    const deleteUser = useMutation({
        mutationFn: async ({ userId, motivo }) => {
            const validId = validateUserId(userId);
            if (!validId) throw new Error('ID inválido');
            
            const response = await api.delete(`/users/${validId}`, { 
                data: motivo ? { motivo } : {} 
            });
            return extractData(response);
        },
        onSuccess: (data, variables) => {
            invalidateUserQueries(variables.userId);
            setAlert(data?.message || 'Usuário excluído!', 'success');
        },
        onError: (error) => {
            setAlert(error.response?.data?.error || error.response?.data?.message || 'Erro ao excluir', 'error');
        }
    });

    const restoreUser = useMutation({
        mutationFn: async (userId) => {
            const validId = validateUserId(userId);
            if (!validId) throw new Error('ID inválido');
            
            const response = await api.post(`/users/${validId}/restaurar`);
            return extractData(response);
        },
        onSuccess: (data, variables) => {
            invalidateUserQueries(variables);
            setAlert(data?.message || 'Usuário restaurado!', 'success');
        },
        onError: (error) => {
            setAlert(error.response?.data?.error || error.response?.data?.message || 'Erro ao restaurar', 'error');
        }
    });

    const resetUserPassword = useMutation({
        mutationFn: async (userId) => {
            const validId = validateUserId(userId);
            if (!validId) throw new Error('ID inválido');

            const response = await api.put(`/users/${validId}/reset-password`);
            return extractData(response);
        },
        onSuccess: (data) => {
            setAlert(data?.message || 'Senha resetada!', 'success');
        },
        onError: (error) => {
            setAlert(error.response?.data?.error || error.response?.data?.message || 'Erro ao resetar', 'error');
        }
    });

    const toggleUserStatus = useMutation({
        mutationFn: async ({ userId, isActive }) => {
            const validId = validateUserId(userId);
            if (!validId) throw new Error('ID inválido');
            
            const response = await api.put(`/users/${validId}`, { isActive });
            return extractData(response);
        },
        onSuccess: (data, variables) => {
            invalidateUserQueries(variables.userId);
            setAlert(data?.message || 'Status alterado!', 'success');
        },
        onError: (error) => {
            setAlert(error.response?.data?.error || error.response?.data?.message || 'Erro ao alterar status', 'error');
        }
    });

    // Retornar todas as mutações
    return {
        createUser,
        vincularUsuarioConta,
        updateUser,
        updateMyProfile,
        updateMyPassword,
        deleteUser,
        restoreUser,
        resetUserPassword,
        toggleUserStatus,
        updateMyPhoto,
        removeMyPhoto,

        isPending: 
            createUser?.isPending || 
            vincularUsuarioConta?.isPending ||
            updateUser?.isPending ||
            deleteUser?.isPending,

        isCreatingUser: createUser?.isPending || false,
        isVinculandoConta: vincularUsuarioConta?.isPending || false,
        isUpdatingUser: updateUser?.isPending || false,
        isUpdatingProfile: updateMyProfile?.isPending || false,
        isDeletingUser: deleteUser?.isPending || false,
        isRestoringUser: restoreUser?.isPending || false,
        isResettingPassword: resetUserPassword?.isPending || false,
        isTogglingStatus: toggleUserStatus?.isPending || false,
        isUploadingMyPhoto: updateMyPhoto?.isPending || false,
        isRemovingMyPhoto: removeMyPhoto?.isPending || false,

        normalizeRole,
        canManageUserPhoto: (targetUserId) => {
            try {
                return currentUser ? canManageUserPhoto(currentUser, targetUserId) : false;
            } catch (error) {
                return false;
            }
        },
        isSelfUser: (targetUserId) => {
            try {
                return currentUser ? isSelf(currentUser, targetUserId) : false;
            } catch (error) {
                return false;
            }
        },
        isMatrizAdminUser: () => {
            try {
                return currentUser ? isMatrizAdmin(currentUser) : false;
            } catch (error) {
                return false;
            }
        }
    };
};

// Aliases para compatibilidade
const useMutateUsers = useUserMutations;
const useUserMutationsSimple = useUserMutations;

// ==============================================================================
// ✅ HOOK COMPOSTO PRINCIPAL
// ==============================================================================

const useUser = () => {
    const { data: currentUser } = useMe();
    const mutations = useUserMutations(currentUser);
    
    return {
        useUsers,
        useUserById,
        useMe,
        useMyStatistics,
        useUserVinculos,
        useUsersByAccount,
        useDeletedUsers,
        useIsProfessionalActive,
        useUserImages,
        useFranquiaAdmins,
        
        currentUser,
        
        ...mutations,
        
        buildQueryParams,
        extractData,
        validateUserId,
        formatUserData,
        normalizeRole,
        
        permissions: {
            canCreateUser: currentUser?.role?.permissions?.includes('equipe:create') || false,
            canEditUser: currentUser?.role?.permissions?.includes('equipe:edit') || false,
            canDeleteUser: currentUser?.role?.permissions?.includes('equipe:delete') || false,
            canViewDeleted: currentUser?.role?.permissions?.includes('usuarios_excluidos:view') || false,
            canLinkAccount: currentUser?.role?.permissions?.includes('equipe:vincular_conta') ||
                            isMatrizAdmin(currentUser),
            isMatrizAdmin: isMatrizAdmin(currentUser),
            isFranquiaAdmin: isFranquiaAdminUser(currentUser),
            isContaAdmin: isContaAdminUser(currentUser)
        }
    };
};

// ==============================================================================
// ✅ EXPORTAÇÕES FINAIS (TODAS AS QUE OS COMPONENTES PRECISAM)
// ==============================================================================

export default useUser;

export {
    // Queries
    useUsers,
    useUserById,
    useMe,
    useMyStatistics,
    useDeletedUsers,
    useIsProfessionalActive,
    useUserImages,
    useUsersByAccount,
    useFranquiaAdmins,
    useFranquiaAdminMutations,
    useUserVinculos,

    // Mutations - TODOS OS QUE SÃO USADOS NOS COMPONENTES
    useUserMutations,
    useMutateUsers,        // ADICIONADO! (alias)
    useUserMutationsSimple, // ADICIONADO! (alias)
    
    // Utilitários
    buildQueryParams,
    extractData,
    validateUserId,
    formatUserData,
    normalizeRole
};